import {observer} from 'mobx-react';
import {ChangeEvent, Dispatch, SetStateAction, useEffect, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {Tooltip, Form, Dropdown, Radio} from 'antd';
import {CurrentPageStore} from '@/store/content-optimizer/currentPage';
import Text from 'antd/lib/typography/Text';
import isURL from 'validator/lib/isURL';
import {getLocalesByKey as t} from '@/utils/translation';
import {Button as CommonBtn, CommonInput} from '@/components/common-components/components';
import {Loader} from '../../../Utils/loader';
import {addProtocolToDomain, sanitizeUrl} from '@/utils/url';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPen, faCaretDown} from '@fortawesome/pro-solid-svg-icons';
import {Button} from '@/components/common-components/v2/Button';
import {Checkbox} from '@/components/common-components/v2/Checkbox';
import {Input} from '@/components/common-components/v2';
import {SidebarTooltip} from '../../../Common/SideBarTooltip/sidebarTooltip';
import {isScaPublicPage} from '@/utils/url';
// import {faChevronDown, faChevronUp} from '@fortawesome/pro-regular-svg-icons';
// import { toJS } from 'mobx';
// import { LoadingOutlined } from '@ant-design/icons';

interface Props {
  store: CurrentPageStore;
  currentView?: 'links' | 'terms';
  onViewChange?: Dispatch<SetStateAction<any>>;
  visibleKeywords?: any[];
  updateKeywords?: (kwArray) => void;
  onFilter: (query: string[]) => void;
  viewSuggestionsOption: boolean;
  linksDropDownFromSuggestion: 'links' | 'terms';
}

export const SuggestionsDataController = observer(({
  store,
  currentView,
  onViewChange,
  visibleKeywords,
  updateKeywords,
  onFilter,
  viewSuggestionsOption,
  linksDropDownFromSuggestion,
}: Props) => {
  const [showEditor, setShowEditor] = useState(false);
  const [isCloseClicked, setIsCloseClicked] = useState(false);
  const [form] = Form.useForm();
  const [inputValue, setInputValue] = useState<string>(store?.content?.hostname ?? store?.content?.importedFromUrl ?? '');
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const isNotPublic = !isScaPublicPage();

  // const [isClustersExpanded, setIsClustersExpanded] = useState<boolean>(false);
  const [termsSearchQuery, setTermsSearchQuery] = useState<string[]>([]);
  const [termsSearchInputValue, setTermsSearchInputValue] = useState<string>('');
  const {Group} = Radio;
  useEffect(() => {
    onViewChange(linksDropDownFromSuggestion);
  }, [linksDropDownFromSuggestion]);


  // const secondAntIcon = <LoadingOutlined style={{fontSize: '12px important', color: '#7f4ead'}} spin />;


  const onCheckboxClicked = (status: boolean, keyword: string) => {
    const filteredKw = visibleKeywords;
    const elemId = filteredKw.findIndex(kw => kw.keyword === keyword);

    filteredKw[elemId].active = status;
    updateKeywords(filteredKw);
  };

  // const handleOnClusterClick = (clusterName: string) => {
  //   let filteredSearch = termsSearchInputValue[0] ? termsSearchInputValue.split(',')?.map(item => item.trim()) : [];

  //   if (filteredSearch?.includes(clusterName)) {
  //     filteredSearch = filteredSearch.filter(item => item !== clusterName);
  //   } else {
  //     filteredSearch.push(clusterName);
  //   }
  //   setTermsSearchInputValue(filteredSearch.join(', '));

  //   onFilter(filteredSearch);
  // };


  const termSearchHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setTermsSearchInputValue(input);

    const formatInput = input.split(',');
    const trimmedArr: string[] = [];
    for (let i = 0; i < formatInput.length; i++) {
      if (!termsSearchQuery.includes(formatInput[i].trim())) {
        trimmedArr.push(formatInput[i].trim());
      }
    }

    setTermsSearchQuery(trimmedArr);
    onFilter(trimmedArr);
  };

  const hostnameEditor = <EditorContainer show={showEditor}>
    <FormStyled
      form={form}
      onFinish={async () => {
        if (!isCloseClicked) {
          try {
            await store?.updateHostname(inputValue);
            await store?.getInternalLinkSuggestionsv2(store?.content?.uuid, true);
            setShowEditor(false);
          } catch (e) {
            return Promise.reject(e);
          }
        }
      }}
      initialValues={{
        urlInput: inputValue,
      }}>
      <Text type='secondary'>{'Page URL'}</Text>
      <Form.Item
        name='urlInput'
        rules={[
          {
            required: true,
            message: t('url-is-required'),
          },
          () => ({
            validator(_, value) {
              if (!value || isURL(value.trim(), {require_protocol: false})) {
                return Promise.resolve();
              }
              return Promise.reject(t('url-not-valid'));
            },
          }),
        ]}
      >
        <InputStyeld
          variant='light'
          name='1'
          onChange={e => {
            setInputValue(e.target.value);
            if (!e.target.value) {
              setSubmitDisabled(true);
            } else {
              setSubmitDisabled(false);
            }
          }
          }
          placeholder={t('enter-url')} />

      </Form.Item>
      <ButtonContainer>
        <CancelButton
          onClick={e => {
            e.preventDefault();
            setInputValue(store?.content?.hostname ?? store?.content?.importedFromUrl ?? '');
            setShowEditor(false);
          }}
          disabled={store?.isUpdatingHostname}
        >
          {store?.isUpdatingHostname ? <Loader width={71}/> : t('cancel')}
        </CancelButton>
        <UpdateButton
          buttonType='primary'
          type='submit'
          disabled={submitDisabled}
          onClick={() => setIsCloseClicked(false)}
        >
          {store?.isUpdatingHostname ? <Loader width={135}/> : 'Update'}
        </UpdateButton>
      </ButtonContainer>

    </FormStyled>
  </EditorContainer>;

  const topMessage = <HostnameContainer>
    {hostnameEditor}
    {isNotPublic && (store?.content?.importedFromUrl || store?.content?.hostname ? <>
      <span style={{width: 'calc(100% - 50px)', display: 'flex'}}>
    Suggestions shown for <Tooltip title={store?.content?.hostname ?? store?.content?.importedFromUrl}>
          <HostnameLink href={addProtocolToDomain(store?.content?.hostname ?? store?.content?.importedFromUrl)} target='_blank' rel='noreferrer'>
            {sanitizeUrl(store?.content?.hostname ?? store?.content?.importedFromUrl)}
          </HostnameLink>
        </Tooltip>
      </span>
      <HostnameLinkBtnContainer onClick={() => setShowEditor(!showEditor)} style={{marginLeft: 'auto'}}>
        <FontAwesomeIcon icon={faPen} style={{fontSize: 14, color: '#34AEF3'}}/>
      </HostnameLinkBtnContainer>
    </> : <>
    To get Link Suggestions <AddNewUrlBtn onClick={() => setShowEditor(!showEditor)}>specify a Page URL</AddNewUrlBtn>
    </>)}
  </HostnameContainer>;

  const viewChangeDropdown = <ViewDropdownContainer>
    <div style={{display: 'flex', flexDirection: 'column'}} onClick={e => e.stopPropagation()}>
      <Group value={currentView} onChange={e => onViewChange(e.target.value)}>
        <RadioStyled value={'links'}>Internal Link Suggestions</RadioStyled>
        <RadioStyled value={'terms'}>External Link Suggestions</RadioStyled>
      </Group>
    </div>
  </ViewDropdownContainer>;

  const keywordsDropdown = <KeywordsDropdownContainer>
    <OverlayTitle>Show Internal Link Suggestions for following target keywords:</OverlayTitle>
    <div style={{display: 'flex', flexDirection: 'column'}} onClick={e => e.stopPropagation()}>
      {visibleKeywords?.map((kw, id) => {
        return <CheckboxStyled key={id} checked={kw.active} onChange={e => onCheckboxClicked(e.target.checked, kw.keyword)}>{kw.keyword}</CheckboxStyled>;
      })}
    </div>
  </KeywordsDropdownContainer>;

  const viewControlls = <ViewControllsContainer>
    <SidebarTooltip title={currentView === 'links' ? 'A hyperlink from one page on a website to another within the same website.' : 'External links connect your pages to other websites'}>
      <Dropdown overlay={viewChangeDropdown} placement='bottomCenter' trigger={['click']}>
        <ButtonStyled viewSuggestionsOption={viewSuggestionsOption}>{currentView === 'links' ? 'Internal Links' : 'External links'}<FontAwesomeIcon icon={faCaretDown} style={{marginLeft: 5}}/></ButtonStyled>
      </Dropdown>
    </SidebarTooltip>

    {currentView === 'links' ? <Dropdown trigger={['click']} overlay={keywordsDropdown} placement='bottomCenter'>
      <ButtonStyled>Keywords <span style={{marginLeft: 5, color: '#fff'}}>{visibleKeywords?.filter(kw => kw.active)?.length}</span> / <span style={{color: '#A3A4A4'}}>{visibleKeywords?.length}</span><FontAwesomeIcon icon={faCaretDown} style={{marginLeft: 5}}/></ButtonStyled>
    </Dropdown> : null}

  </ViewControllsContainer>;

  const SearchAndSort = <SearchAndSortWraper>
    <SearchContainer>
      <InputStyled variant='dark' placeholder='Search' value={termsSearchInputValue} onChange={e => termSearchHandler(e)}/>
    </SearchContainer>
    {/* <ClustersWrapper>
      {store?.isAnalyzing ? <Spin indicator={secondAntIcon}/> : <ClustersContainer expandedClusters={isClustersExpanded}>
        {toJS(store.content?.internalLinkSuggestions)?.map((cluster, id) => {
          return <Tooltip placement='bottom'
            key={id}
            overlayInnerStyle={{background: '#000', width: 200, borderRadius: 5}}
            title={<div>
              <ClusterTooltipSingleRow>
                <ClusterTooltipLabel>Link Suggestions in cluster:</ClusterTooltipLabel>
                <ClusterTooltipValue>{cluster.results?.length}</ClusterTooltipValue>
              </ClusterTooltipSingleRow>
            </div>}>
            <SingleClusterContainer activeCluster={termsSearchInputValue?.split(',')?.filter(item => item.trim() === cluster.keyword).length > 0}
              onClick={() => handleOnClusterClick(cluster.keyword)}>{`#${cluster.keyword} - ${cluster.results?.length}`}
            </SingleClusterContainer>
          </Tooltip>;
        })}
      </ClustersContainer>}
      <FontAwesomeIcon icon={isClustersExpanded ? faChevronUp : faChevronDown} style={{cursor: 'pointer', marginLeft: 'auto'}} onClick={() => setIsClustersExpanded(!isClustersExpanded)}/>
    </ClustersWrapper> */}
  </SearchAndSortWraper>;

  return <Wrapper>
    {topMessage}
    {viewControlls}
    {SearchAndSort}
  </Wrapper>;
});

const Wrapper = styled.div`
  margin-bottom: 10px;
`;

const EditorContainer = styled.div<{show: boolean}>`
  display: ${p => p.show ? 'block' : 'none'};
  background-color: #fff;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  position: absolute;
  top: 20px;
  right: 0;
  padding: 15px 10px;
  width: 300px;
  z-index: 1;
 `;
const FormStyled = styled(Form)`
  .ant-form-item {
    margin-bottom: 10px;
  }

  .ant-form-item-explain.ant-form-item-explain-error {
    div {
      color: #ff4d4f;
    }
  }
 `;

const HostnameContainer = styled.div`
  position: relative;
  display: flex;
  font-size: 12px;
  margin-bottom: 10px;
`;
const HostnameLink = styled.a`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: calc(100% - 150px);
  margin-left: 5px;
  color: #34AEF3;
`;
const HostnameLinkBtnContainer = styled.div`
  width: 14px;
  height: 14px;
  margin-left: 10px;
  cursor: pointer;
`;

const ViewControllsContainer = styled.div`
  display: flex;
`;
const AddNewUrlBtn = styled.div`
  font-size: 12px;
  margin-left: 5px;
  color: #40a9ff !important;
  text-decoration: underline;
  cursor: pointer;
`;
const InputStyeld = styled(CommonInput)`
 `;
const ButtonContainer = styled(Form.Item)`
  .ant-form-item-control-input-content {
    display: flex;
    justify-content:flex-end;
  }
 `;

const CancelButton = styled(CommonBtn)`
  background-color: #e8e8e8;
  border: 1px solid #e8e8e8;
  padding: 5px 15px;

  span {
    color: #4E5156 !important;
  }

  &:hover, &.active, &.focus {
    background-color: #e8e8e8;
    border: 1px solid #e8e8e8;
  }
 `;
const UpdateButton = styled(CommonBtn)`
  padding: 5px 15px;
  margin-left: 10px;
 `;
const ViewDropdownContainer = styled.div`
  background-color: #fff;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  width: 255px;
  padding: 12px 20px;
  border-radius: 5px;
`;
const RadioStyled = styled(Radio)`
    .ant-radio-checked .ant-radio-inner {
      background-color: #7f4ead !important;
      border-color: #7f4ead !important;

      &::after {
        background-color: #fff !important;
      }
    }
`;
const blinkAnimation = keyframes`
    0%, 100% {
    background-color: #2D2F34;
  }
  50% {
    background-color: #6F7177;
  }
`;
const ButtonStyled = styled(Button)<{viewSuggestionsOption?: boolean}>`
  background-color: #323539;
  border: 1px solid transparent;;
  font-size: 13px;
  padding: 8px 14px;
  margin: 0 9px 0 0 !important;

  &:hover, &:focus {
    background-color: transparent !important;
  }
  animation: ${p => p.viewSuggestionsOption ? css`${blinkAnimation} 2s;` : ''}; 
  animation-delay: .5s;
  `;
const KeywordsDropdownContainer = styled.div`
  background-color: #fff;
  box-shadow: 0px 6px 25px rgba(0, 0, 0, 0.15);
  width: 230px;
  padding: 12px 20px;
  border-radius: 5px;
`;


const CheckboxStyled = styled(Checkbox)`
  margin-left: 0px !important;
  font-weight: 500 !important;
`;

const OverlayTitle = styled.p`
  color: #4E5156;
  font-size: 12px;
`;

// const ClusterTooltipSingleRow = styled.div`
//   display: flex;
//   justify-content: space-between
// `;
// const ClusterTooltipLabel = styled.div`
//   font-size: 12px;
//   font-weight: 400;
//   color: #E8E8E8;
// `;

// const ClusterTooltipValue = styled.div`
//   font-size: 12px;
//   font-weight: 500;
//   color: #FFFFFF;
// `;

const SearchAndSortWraper= styled.div `
  margin-top: 10px;
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

// const ClustersWrapper = styled.div`
//   display: flex;
// `;
// const ClustersContainer = styled.div<{expandedClusters?: boolean}>`
//   display: flex;
//   flex-wrap: wrap;

//   height: ${p => p.expandedClusters ? 'auto' : '25px'};
//   overflow: hidden;
// `;

// const SingleClusterContainer = styled.div<{activeCluster?: boolean}>`
//   background-color: ${p => p.activeCluster ? '#f8a62933' : '#2E3135'};
//   border-radius: 60px;
//   padding: 3px 8px;
//   width: auto;
//   height: 20px;
//   font-size: 11px;
//   margin-right: 6px;
//   margin-bottom: 6px;
//   cursor: pointer;

//   &:hover {
//     background-color: #4B4D51 !important;
//   }
// `;

const InputStyled = styled(Input)`
  height: 32px;
`;
